import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Subject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AccessibilityWebShopService {
    private accessabiltyfontsizeenabled: boolean = false;
    private accessabiltycolorsenabled: boolean = false;
    private isAccessibilityModeActive: boolean = false;
    public accessabiltynoImagesMode: boolean = false;
    public accessabiltyreducedMotionMode: boolean = false;
    private renderer: Renderer2;
    private originalColors: Map<string, string> = new Map();
    /** true, sobald applyDynamicColors einmal Inline-Werte an das Wurzelelement geschrieben hat - Vorbedingung fuer refreshBaseColors, siehe dort */
    private hasPinnedColorsInline: boolean = false;

    private _accessibilityModeActiveSubject: Subject<boolean> = new Subject<boolean>();
    public get accessibilityModeActive$(): Observable<boolean> {
        return this._accessibilityModeActiveSubject.asObservable();
    }

    constructor(rendererFactory: RendererFactory2) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    public toggleAccessibility(): void {
        const me = this;
        me.accessabiltyfontsizeenabled = !this.accessabiltyfontsizeenabled;
        me.applyAccessibilitySettings();
    }

    public isAccessabiltyFontSizeEnabled(): boolean {
        return this.accessabiltyfontsizeenabled;
    }

    public isAccessabiltyColorsEnabled(): boolean {
        return this.accessabiltycolorsenabled;
    }

    public isNoImagesModeEnabled(): boolean {
        return this.accessabiltynoImagesMode;
    }

    public isReducedMotionModeEnabled(): boolean {
        return this.accessabiltyreducedMotionMode;
    }

    // Alle semantischen CSS-Variablen — Reihenfolge entspricht den Kategorien in _custom-color-variable.scss
    // Same 70-name set as WEBSHOP_COLOR_TOKEN_NAMES (webshopColorTokens.model.ts), independently maintained here
    // for the a11y contrast replacement; keep both (and ColorTokenDefaults.vb) in sync if the token set changes.
    private static readonly COLOR_VARS: ReadonlyArray<string> = [
        // Font Colors
        '--color-font-primary',
        '--color-font-primary-sub',
        '--color-font-secondary',
        '--color-font-secondary-sub',
        '--color-font-accent',
        '--color-font-accent-2',
        '--color-font-home-label',
        '--color-font-pageheader',
        '--color-font-pagesubheader',
        '--color-font-favoritebar',
        '--color-font-content-header',
        '--color-font-content-subheader',
        '--color-font-content-body',
        '--color-font-btn-modal',
        '--color-font-btn-page',
        '--color-font-footer',
        '--color-font-btn-modal-disabled',
        '--color-font-btn-page-disabled',
        '--color-font-tab',
        '--color-font-header',
        '--color-font-menu',
        '--color-font-content-body-disabled',
        '--color-font-sidenav',
        // Background Colors
        '--color-bg-primary',
        '--color-bg-secondary',
        '--color-bg-header',
        '--color-bg-home-label',
        '--color-bg-pageheader',
        '--color-bg-btn-modal',
        '--color-bg-btn-page',
        '--color-bg-app',
        '--color-bg-btn-wide',
        '--color-bg-btn-modal-disabled',
        '--color-bg-btn-wide-disabled',
        '--color-bg-btn-page-disabled',
        '--color-bg-ripple',
        '--color-bg-user-profile',
        '--color-bg-progress',
        '--color-bg-footer',
        '--color-bg-calendar-selected',
        '--color-bg-statusbar',
        '--color-bg-user-circle',
        '--color-bg-checkbox',
        '--color-bg-sidenav',
        '--color-bg-nav-back',
        '--color-bg-mixed',
        // Accent & Highlight Colors
        '--color-accent',
        '--color-accent-2',
        '--color-accent-tabs-underline',
        '--color-accent-highlight',
        '--color-accent-progress',
        '--color-accent-sidenav',
        // Button Colors
        '--color-btn-wide',
        '--color-btn-wide-disabled',
        '--color-btn-nav-back',
        // Border Colors
        '--color-border-text',
        '--color-border-item',
        '--color-border-nav-back',
        // State Colors
        '--color-state-error',
        '--color-state-false',
        '--color-state-option-selected',
        // Icons & Miscellaneous
        '--color-icon-co2',
        '--color-icon-like',
        '--color-icon-like-disabled',
        '--color-icon-header',
        '--color-current',
        '--color-rating',
        '--color-user-profile-info',
        '--color-item-description',
        '--color-detail-page-header',
    ];

    private applyDynamicColors(root: HTMLElement): void {
        const computedStyles = getComputedStyle(root);

        for (const colorVar of AccessibilityWebShopService.COLOR_VARS) {
            // --color-font-primary → --a11y-color-font-primary
            const a11yVar = colorVar.replace('--color-', '--a11y-color-');

            // Originalfarbe nur einmal speichern
            if (!this.originalColors.has(colorVar)) {
                const originalValue = computedStyles.getPropertyValue(colorVar).trim();
                if (originalValue) {
                    this.originalColors.set(colorVar, originalValue);
                }
            }

            const currentColor = root.style.getPropertyValue(colorVar);
            const originalColor = this.originalColors.get(colorVar) || '';
            const a11yColor = computedStyles.getPropertyValue(a11yVar).trim();

            const newColor = this.accessabiltycolorsenabled ? (a11yColor || originalColor) : originalColor;

            if (newColor) {
                if (currentColor !== newColor) {
                    root.style.setProperty(colorVar, newColor);
                }
            } else {
                root.style.removeProperty(colorVar);
            }
        }
        this.hasPinnedColorsInline = true;
    }

    /**
     * Verwirft die zwischengespeicherten Originalfarben und baut sie aus dem aktuellen
     * Stylesheet-Stand neu auf. Aufrufer ist WebshopColorTokensService nach jedem Schreiben
     * der portalgepflegten Farbrollen.
     *
     * Hintergrund: applyDynamicColors schreibt die Farben mit root.style.setProperty als
     * Inline-Style an das Wurzelelement. Inline schlaegt jedes Stylesheet, also auch den
     * <style>-Block, in den WebshopColorTokensService die Portal-Werte schreibt. Ohne diesen
     * Aufruf blieben alle Tokens aus COLOR_VARS auf dem Stand eingefroren, der galt, als der
     * Accessibility-Dialog zuletzt angewandt wurde - und originalColors wurde bewusst nur
     * einmal gefuellt, wuerde den veralteten Wert also auch bei jedem spaeteren Anwenden
     * wieder herstellen.
     *
     * Zwei Bedingungen, die hier nicht verletzt werden duerfen:
     *   1. Nur wirksam, wenn ueberhaupt schon inline gepinnt wurde. Sonst wuerde dieser
     *      Aufruf das Pinning bei jedem Farb-Sync erst erzeugen und das Problem herstellen,
     *      das er loesen soll.
     *   2. Die Inline-Properties muessen VOR dem Neulesen entfernt werden. getComputedStyle
     *      liefert sonst genau den Inline-Wert zurueck, und der Cache wuerde mit dem alten
     *      Stand neu befuellt - die Aktualisierung waere wirkungslos.
     */
    public refreshBaseColors(): void {
        const me = this;
        if (!me.hasPinnedColorsInline) {
            return;
        }
        const root = document.documentElement;
        for (const colorVar of AccessibilityWebShopService.COLOR_VARS) {
            root.style.removeProperty(colorVar);
        }
        me.originalColors.clear();
        me.applyDynamicColors(root);
    }

    public setAccessibilityModes(fontSizeEnabled: boolean, colorsEnabled: boolean, noImagesEnabled: boolean,
        reducedMotionEnabled: boolean): void {
        const me = this;
        me.accessabiltyfontsizeenabled = fontSizeEnabled;
        me.accessabiltycolorsenabled = colorsEnabled;
        me.isAccessibilityModeActive = fontSizeEnabled || colorsEnabled || noImagesEnabled || reducedMotionEnabled;
        me.accessabiltynoImagesMode = noImagesEnabled;
        me.accessabiltyreducedMotionMode = reducedMotionEnabled;
        me.applyAccessibilitySettings();
        me._accessibilityModeActiveSubject.next(me.isAccessibilityModeActive);
    }

    public getAccessibilityModeActive(): boolean {
        return this.isAccessibilityModeActive;
    }
    public setNoImagesMode(enabled: boolean): void {
        const root = document.documentElement;
        this.toggleClass(root, 'no-images-mode', enabled);
    }

    public setReducedMotionMode(enabled: boolean): void {
        const root = document.documentElement;
        this.toggleClass(root, 'reduced-motion-mode', enabled);
    }

    public applyAccessibilitySettings(): void {
        const me = this;
        const root = document.documentElement;
        me.toggleClass(root, 'accessibilitycolors-mode', me.accessabiltycolorsenabled); // Apply color mode
        me.toggleClass(root, 'accessibilityfontsize-mode', me.accessabiltyfontsizeenabled); // Apply font size mode
        me.toggleClass(root, 'no-images-mode', me.accessabiltynoImagesMode); // Apply no images mode
        me.toggleClass(root, 'reduced-motion-mode', me.accessabiltyreducedMotionMode); // Apply reduced motion mode
        me.applyDynamicColors(root);
    }

    private toggleClass(element: HTMLElement, className: string, condition: boolean): void {
        if (condition) {
            element.classList.add(className);
        } else {
            element.classList.remove(className);
        }
    }
}